export interface Transform {
  scale: number;
  offsetX: number;
  offsetY: number;
}

export function createTransform(scale: number, offsetX: number, offsetY: number): Transform {
  return { scale, offsetX, offsetY };
}

export function applyTransform(ctx: CanvasRenderingContext2D, transform: Transform): void {
  ctx.setTransform(transform.scale, 0, 0, transform.scale, transform.offsetX, transform.offsetY);
}

export function invertTransform(transform: Transform): Transform {
  const invScale = 1 / transform.scale;
  return {
    scale: invScale,
    offsetX: -transform.offsetX * invScale,
    offsetY: -transform.offsetY * invScale
  };
}

export function canvasToImageCoords(
  canvasX: number, 
  canvasY: number, 
  transform: Transform,
  imageWidth: number,
  imageHeight: number
): { x: number; y: number } {
  const inverted = invertTransform(transform);
  const x = Math.round((canvasX - transform.offsetX) / transform.scale);
  const y = Math.round((canvasY - transform.offsetY) / transform.scale);
  
  // Clamp to image bounds
  return {
    x: Math.max(0, Math.min(imageWidth - 1, x)),
    y: Math.max(0, Math.min(imageHeight - 1, y))
  };
}

export function imageToCanvasCoords(
  imageX: number, 
  imageY: number, 
  transform: Transform
): { x: number; y: number } {
  return {
    x: imageX * transform.scale + transform.offsetX,
    y: imageY * transform.scale + transform.offsetY
  };
}

// Status color mapping for canvas drawing
export const STATUS_COLORS = {
  open: '#f59e0b',      // amber-500
  inProgress: '#0ea5e9', // sky-500
  resolved: '#10b981',   // emerald-500
  skipped: '#6b7280'     // gray-500
} as const;

// Smart boundary calculation for preventing over-panning and under-zooming
export function calculateSmartBoundaries(
  canvasWidth: number,
  canvasHeight: number,
  imageWidth: number,
  imageHeight: number,
  scale: number,
  absoluteMinScale: number
): {
  minOffsetX: number;
  maxOffsetX: number;
  minOffsetY: number;
  maxOffsetY: number;
  minScale: number;
} {
  // Use the provided absolute minimum scale (usually fit scale)
  const minScale = absoluteMinScale;
  
  // Use current scale, but ensure it's not below absolute minimum
  const effectiveScale = Math.max(scale, minScale);
  
  // Calculate the scaled image dimensions
  const scaledWidth = imageWidth * effectiveScale;
  const scaledHeight = imageHeight * effectiveScale;
  
  // Calculate boundary constraints
  let minOffsetX, maxOffsetX, minOffsetY, maxOffsetY;
  
  if (scaledWidth > canvasWidth) {
    // Image is wider than canvas - allow horizontal panning
    maxOffsetX = 0; // Right edge of image touches right edge of canvas
    minOffsetX = canvasWidth - scaledWidth; // Left edge of image touches left edge of canvas
  } else {
    // Image fits within canvas width - center it horizontally
    const centerX = (canvasWidth - scaledWidth) / 2;
    minOffsetX = maxOffsetX = centerX;
  }
  
  if (scaledHeight > canvasHeight) {
    // Image is taller than canvas - allow vertical panning
    maxOffsetY = 0; // Top edge of image touches top edge of canvas
    minOffsetY = canvasHeight - scaledHeight; // Bottom edge of image touches bottom edge of canvas
  } else {
    // Image fits within canvas height - center it vertically
    const centerY = (canvasHeight - scaledHeight) / 2;
    minOffsetY = maxOffsetY = centerY;
  }
  
  return {
    minOffsetX,
    maxOffsetX,
    minOffsetY,
    maxOffsetY,
    minScale
  };
}

// Apply smart boundary constraints to transform values
export function constrainTransform(
  scale: number,
  offsetX: number,
  offsetY: number,
  canvasWidth: number,
  canvasHeight: number,
  imageWidth: number,
  imageHeight: number,
  allowTemporaryOverscroll: boolean = false,
  userMinScale?: number
): { scale: number; offsetX: number; offsetY: number } {
  // Calculate minimum scale that would prevent canvas background from showing
  const scaleX = canvasWidth / imageWidth;
  const scaleY = canvasHeight / imageHeight;
  const coverMinScale = Math.max(scaleX, scaleY);
  
  // For fit scale (allowing some canvas background), use the smaller scale
  const fitMinScale = Math.min(scaleX, scaleY);
  
  // Use user-provided minScale if available, otherwise use fit scale
  const absoluteMinScale = userMinScale !== undefined ? userMinScale : fitMinScale;
  
  const boundaries = calculateSmartBoundaries(
    canvasWidth,
    canvasHeight,
    imageWidth,
    imageHeight,
    scale,
    absoluteMinScale
  );
  
  // Only enforce minimum scale to prevent showing canvas background
  const constrainedScale = Math.max(scale, boundaries.minScale);
  
  // Recalculate boundaries with the constrained scale
  const finalBoundaries = calculateSmartBoundaries(
    canvasWidth,
    canvasHeight,
    imageWidth,
    imageHeight,
    constrainedScale,
    absoluteMinScale
  );
  
  let constrainedOffsetX = offsetX;
  let constrainedOffsetY = offsetY;
  
  // Always center the image when it's smaller than the canvas (min === max)
  // This ensures the image stays centered even during zoom operations
  if (finalBoundaries.minOffsetX === finalBoundaries.maxOffsetX) {
    constrainedOffsetX = finalBoundaries.minOffsetX;
  } else if (!allowTemporaryOverscroll) {
    // Only apply strict offset constraints if not allowing temporary overscroll
    constrainedOffsetX = Math.max(
      finalBoundaries.minOffsetX,
      Math.min(finalBoundaries.maxOffsetX, offsetX)
    );
  }
  
  if (finalBoundaries.minOffsetY === finalBoundaries.maxOffsetY) {
    constrainedOffsetY = finalBoundaries.minOffsetY;
  } else if (!allowTemporaryOverscroll) {
    constrainedOffsetY = Math.max(
      finalBoundaries.minOffsetY,
      Math.min(finalBoundaries.maxOffsetY, offsetY)
    );
  }
  
  return {
    scale: constrainedScale,
    offsetX: constrainedOffsetX,
    offsetY: constrainedOffsetY
  };
}

export function drawIndicator(
  ctx: CanvasRenderingContext2D, 
  x: number, 
  y: number, 
  number: number,
  color: string = '#ef4444',
  status?: string,
  opacity: number = 1
): void {
  const radius = 12;
  
  // Round coordinates to whole pixels for crisp rendering
  const pixelX = Math.round(x);
  const pixelY = Math.round(y);
  
  // Apply opacity to the entire indicator (multiply with existing globalAlpha to preserve parent opacity)
  ctx.save();
  ctx.globalAlpha = ctx.globalAlpha * opacity;
  
  // Disable anti-aliasing for crisp pixel-perfect indicators
  ctx.imageSmoothingEnabled = false;
  
  // Layer 1: Draw shadow
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(pixelX, pixelY, radius, 0, 2 * Math.PI);
  ctx.fill();
  ctx.restore();
  
  // Layer 2: Draw thick white border/ring
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.arc(pixelX, pixelY, radius, 0, 2 * Math.PI);
  ctx.stroke();
  
  // Layer 3: Draw colored circle background
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(pixelX, pixelY, radius, 0, 2 * Math.PI);
  ctx.fill();
  
  // Draw number text
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 12px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(number.toString(), pixelX, pixelY);
  
  // Draw status indicator dot in upper right corner (without shadow)
  if (status && status in STATUS_COLORS) {
    const statusRadius = 3;
    const offsetX = radius * 0.7;  // Position in upper right
    const offsetY = -radius * 0.7;
    const statusX = Math.round(pixelX + offsetX);
    const statusY = Math.round(pixelY + offsetY);
    
    // Draw status dot with white border
    ctx.fillStyle = STATUS_COLORS[status as keyof typeof STATUS_COLORS];
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(statusX, statusY, statusRadius, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
  }
  
  ctx.restore();
}

// Drawing box interface
export interface DrawingBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

// Draw a drawing box on the canvas
export function drawBox(
  ctx: CanvasRenderingContext2D,
  box: DrawingBox,
  color: string,
  opacity: number = 0.3,
  strokeWidth: number = 2,
  indicatorNumber?: number
): void {
  ctx.save();
  
  // Calculate box dimensions
  const x = Math.min(box.startX, box.endX);
  const y = Math.min(box.startY, box.endY);
  const width = Math.abs(box.endX - box.startX);
  const height = Math.abs(box.endY - box.startY);
  
  // Draw semi-transparent fill
  ctx.fillStyle = color;
  ctx.globalAlpha = opacity;
  ctx.fillRect(x, y, width, height);
  
  // Draw solid border
  ctx.globalAlpha = 1;
  ctx.strokeStyle = color;
  ctx.lineWidth = strokeWidth;
  ctx.strokeRect(x, y, width, height);
  
  ctx.restore();
}

// Drawing arrow interface
export interface DrawingArrow {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

// Draw an arrow on the canvas
export function drawArrow(
  ctx: CanvasRenderingContext2D,
  arrow: DrawingArrow,
  color: string,
  strokeWidth: number = 3
): void {
  ctx.save();
  
  const { startX, startY, endX, endY } = arrow;
  
  // Calculate arrow head size based on stroke width
  const headLength = strokeWidth * 5;
  const angle = Math.atan2(endY - startY, endX - startX);
  
  // Draw arrow line
  ctx.strokeStyle = color;
  ctx.lineWidth = strokeWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  
  ctx.beginPath();
  ctx.moveTo(startX, startY);
  ctx.lineTo(endX, endY);
  ctx.stroke();
  
  // Draw arrow head
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(endX, endY);
  ctx.lineTo(
    endX - headLength * Math.cos(angle - Math.PI / 6),
    endY - headLength * Math.sin(angle - Math.PI / 6)
  );
  ctx.lineTo(
    endX - headLength * Math.cos(angle + Math.PI / 6),
    endY - headLength * Math.sin(angle + Math.PI / 6)
  );
  ctx.closePath();
  ctx.fill();
  
  ctx.restore();
}

// Drawing path interface (for free draw)
export interface DrawingPath {
  points: { x: number; y: number }[];
}

// Draw a free-form path on the canvas
export function drawPath(
  ctx: CanvasRenderingContext2D,
  path: DrawingPath,
  color: string,
  strokeWidth: number = 3
): void {
  if (path.points.length < 2) return;
  
  ctx.save();
  
  ctx.strokeStyle = color;
  ctx.lineWidth = strokeWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  
  ctx.beginPath();
  ctx.moveTo(path.points[0].x, path.points[0].y);
  
  for (let i = 1; i < path.points.length; i++) {
    ctx.lineTo(path.points[i].x, path.points[i].y);
  }
  
  ctx.stroke();
  ctx.restore();
}

// Check if a point is inside a drawing box
export function isPointInBox(x: number, y: number, box: DrawingBox): boolean {
  const minX = Math.min(box.startX, box.endX);
  const maxX = Math.max(box.startX, box.endX);
  const minY = Math.min(box.startY, box.endY);
  const maxY = Math.max(box.startY, box.endY);
  
  return x >= minX && x <= maxX && y >= minY && y <= maxY;
}